// Builds the compare-to-canonical (GQV) layer folded into Dependent Variable, Brand
// Equity's GQV stage only -- same shape as ground-truth-sql.ts's Portfolio/Channels
// pattern (always-visible aggregate deviation + a copy-pasteable SQL block), just for
// a single metric (GQV) instead of paired spend/impressions.

export interface GqvGroundTruthWeek {
  weekIso: string;
  mmmValue: number;
  // Already-loaded canonical value for the same week (WeekRow.bpmDependentValue) --
  // only used for the aggregate deviation readout below, not the SQL itself (that
  // re-derives the canonical value live from Redshift).
  bpmValue?: number;
}

// Training-window-wide deviation: sum every week's MMM and canonical values first, then
// compare the totals (MMM / canonical - 1) -- not an average of per-week ratios, same
// rationale as ground-truth-sql.ts's computeAggregateDeviation. Returns undefined when
// there's no canonical total to divide by. Only sums a week into EITHER total when its
// bpmValue is defined -- GQV/BPM/YouGov ground truth commonly lags the model's own
// recent weeks (bpmValue undefined, not 0, for those -- see this interface's own doc
// comment), and summing mmmValue for those weeks unconditionally while `?? 0`-ing the
// canonical side made the numerator cover more weeks than the denominator, skewing the
// ratio (e.g. +25% instead of ~0%) purely from a coverage gap, not a real mismatch.
export const computeGqvAggregateDeviation = (weeks: GqvGroundTruthWeek[]): number | undefined => {
  const covered = weeks.filter(
    (w): w is GqvGroundTruthWeek & { bpmValue: number } => w.bpmValue !== undefined
  );
  const totals = covered.reduce(
    (acc, w) => ({ mmm: acc.mmm + w.mmmValue, bpm: acc.bpm + w.bpmValue }),
    { mmm: 0, bpm: 0 }
  );
  if (totals.bpm === 0) {
    return undefined;
  }
  return totals.mmm / totals.bpm - 1;
};

// Same convention as data-qa-utils.ts's bucketEndDate / ground-truth-sql.ts's
// dateTruncUnitFor (duplicated, not imported -- this module is deliberately
// standalone). Must match weeks[].weekIso's own bucket grain (bucketWeeksByGranularity,
// backend), or the join below silently compares mismatched grains.
const dateTruncUnitFor = (reviewGranularity: string | undefined): 'week' | 'month' | 'day' => {
  const granularity = (reviewGranularity ?? '').trim().toLowerCase();
  if (granularity === 'weekly') return 'week';
  if (granularity === 'monthly') return 'month';
  return 'day';
};

interface BuildGqvGroundTruthSqlArgs {
  company: string;
  // Should already be scoped to the model's training window (fencepost) -- weeks
  // outside it aren't part of what the model trains on.
  weeks: GqvGroundTruthWeek[];
  // dataset.reviewGranularity -- see dateTruncUnitFor above. Defaults to weekly (the
  // pre-fix bucketing) when omitted, rather than silently degrading to daily for an
  // existing caller that hasn't been updated yet.
  reviewGranularity?: string;
}

// Uses the fully-qualified `main_db.public.gqv_v2` (unlike the backend's own
// `streaming_gqv.gqv_v2` in mmmDataQA.ts's fetchGqvGroundTruth): this SQL runs from an
// external DataGrip session against a different cluster ("mobius"), which needs the
// main_db.public. qualifier to reach the shared table; the backend's Redshift Data API
// connection instead has it locally in the streaming_gqv schema, no cross-database
// reference needed -- confirmed against real `away` data 2026-08-26.
export const buildGqvGroundTruthSql = ({
  company,
  weeks,
  reviewGranularity = 'weekly'
}: BuildGqvGroundTruthSqlArgs): string => {
  if (weeks.length === 0) {
    return "-- No weeks in the model's training window yet.";
  }

  const truncUnit = dateTruncUnitFor(reviewGranularity);
  const escapedCompany = company.replace(/'/g, "''");
  const mmmRows = weeks
    .map(w => `  select '${w.weekIso}' as week, ${w.mmmValue.toFixed(2)} as mmm_gqv`)
    .join('\n  union all\n');

  return [
    'with mmm as (',
    mmmRows,
    '),',
    'gqv as (',
    '  select',
    `    to_char(date_trunc('${truncUnit}', report_date::date), 'YYYY-MM-DD') as week,`,
    '    sum(indexed_query_volume) as bpm_gqv',
    '  from main_db.public.gqv_v2',
    `  where company = '${escapedCompany}'`,
    "    and query_label = 'BRAND'",
    "    and geo_type = 'DMA_REGION'",
    '    and project_id = (',
    '      select project_id',
    '      from main_db.public.gqv_v2',
    `      where company = '${escapedCompany}'`,
    '      order by report_date desc',
    '      limit 1',
    '    )',
    '  group by 1',
    ')',
    'select',
    '  m.week,',
    '  m.mmm_gqv, g.bpm_gqv, round(m.mmm_gqv / nullif(g.bpm_gqv, 0) - 1, 4) as gqv_deviation',
    'from mmm m',
    'join gqv g on g.week = m.week',
    'order by m.week;'
  ].join('\n');
};
